// verify-subscribe.js - shared, dependency-light Subscribe form for the GTI Verify
// Badge surfaces (kiosk landing after auto-run, and optionally any static verify page).
//
// WHY this exists as its own module: the kiosk (Component B) turns a shared verify
// badge into a "verify + follow" surface, so an anonymous visitor who re-ran a frozen
// std result can opt into the newsletter in one click. Keeping the POST logic here -
// mirroring app.js' landing-footer subscribe verbatim - means the kiosk (and a future
// inlined static page) reuse the exact same double-opt-in contract instead of forking
// a second, subtly-different subscribe path.
//
// It posts to the already-shipped anonymous endpoint /api/email/subscribe (AllowAnonymous,
// no auth/CSRF/cookie). The server does its own lenient LooksLikeEmail validation and is
// the only authority; the client just adds type=email for a nicer prompt. Double opt-in is
// honest in the copy: a single POST does NOT silently subscribe - the server emails a
// confirmation link, so the status text says "check your email to confirm".

// BASE mirrors api.js (const BASE = window.location.origin). api.js does not export it,
// so we resolve it locally to keep this module importable AND inlinable on a static page:
// prefer an explicit override, then the page origin, then a relative-path fallback.
const BASE = (typeof window !== 'undefined' && (window.__gdbsBase || window.location.origin)) || '';

// Small counter so multiple mountSubscribe() instances on one page get unique element ids
// (defensive - ids are also scoped by querying inside `el`, but unique ids avoid label/aria
// collisions if two verify surfaces ever render together).
let _seq = 0;

/**
 * Render a compact newsletter Subscribe form into `el`.
 *
 * @param {HTMLElement} el   - container to render into (its innerHTML is replaced).
 * @param {Object}      opts - { source } cosmetic/local only. The server hardcodes
 *                             source='public'; we keep the arg for symmetry and so a
 *                             caller can label the surface in local analytics later.
 * @returns {HTMLFormElement|null} the created form (for callers that want to hook it).
 */
export function mountSubscribe(el, opts = {}) {
  if (!el) return null;
  const source = opts.source || 'public';
  const uid = 'vsub-' + (++_seq) + '-' + Math.random().toString(36).slice(2, 8);
  const emailId = uid + '-email';
  const statusId = uid + '-status';

  // Inline light styling so the form is legible on the kiosk banner and on any static
  // verify surface, independent of whichever stylesheet (or none) is present. Deliberately
  // self-contained: zero class dependencies beyond api.js' BASE.
  el.innerHTML =
    '<form data-verify-subscribe="' + source + '" novalidate ' +
    'style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin:0;">' +
      '<input id="' + emailId + '" type="email" name="email" required ' +
      'placeholder="you@email.com" autocomplete="email" ' +
      'style="flex:1 1 180px;min-width:160px;padding:8px 10px;border:1px solid #cbd5e1;' +
      'border-radius:6px;font-size:13px;color:#0f172a;background:#ffffff;box-sizing:border-box;">' +
      '<button type="submit" ' +
      'style="padding:8px 14px;border:0;border-radius:6px;background:#7c3aed;color:#ffffff;' +
      'font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap;">Subscribe</button>' +
    '</form>' +
    '<span id="' + statusId + '" class="status" role="status" aria-live="polite" ' +
    'style="display:block;margin-top:6px;font-size:12px;min-height:16px;color:#64748b;"></span>';

  const form = el.querySelector('form');
  const input = el.querySelector('#' + emailId);
  const status = el.querySelector('#' + statusId);
  const btn = form.querySelector('button[type="submit"]');

  // Submit handler - mirrors app.js:1030-1046 (the landing-footer subscribe) verbatim,
  // only the DOM ids and inline styling differ. Handles BOTH response keys: a 200 OK
  // returns { message }, a 400 returns { error } (see EmailController).
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = (input.value || '').trim();
    if (!email) return;

    // Disable the button during flight so a double-click can't double-POST.
    btn.disabled = true;
    btn.style.opacity = '0.7';
    btn.style.cursor = 'wait';
    status.textContent = 'Submitting...';
    status.style.color = '#64748b';

    try {
      const res = await fetch(BASE + '/api/email/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        // Honest double-opt-in copy: the POST only requests a confirmation email.
        status.textContent = data.message || 'Almost there - check your email to confirm.';
        status.style.color = '#16a34a';
        form.reset();
      } else {
        status.textContent = data.error || 'Enter a valid email address.';
        status.style.color = '#dc2626';
      }
    } catch {
      status.textContent = 'Network error. Please try again.';
      status.style.color = '#dc2626';
    } finally {
      btn.disabled = false;
      btn.style.opacity = '';
      btn.style.cursor = 'pointer';
    }
  });

  return form;
}
